import React, { useEffect, useState, useRef, type ChangeEvent } from 'react';
import { Link } from 'react-router-dom';
import API_ENDPOINTS from '../../api/apiConfig';
import { env } from '../../config/env';
import type { CityItem, StateItem, UserProfile as UserProfileData } from '../../types';
import '../../assets/css/theme.css';
import '../../assets/css/shop.css';      // shared page-header band (.ffs-hero*)
import '../../assets/css/account.css';

/** ISO timestamp -> "19 August 2026" (falls back to the raw value). */
const formatMemberSince = (raw?: string): string => {
  if (!raw) return '—';
  const d = new Date(raw);
  return Number.isNaN(d.getTime())
    ? raw
    : d.toLocaleDateString('en-IN', { day: 'numeric', month: 'long', year: 'numeric' });
};

/** Show only the last 4 characters of an ID number (Aadhaar / PAN). */
const maskId = (value?: string): string =>
  value ? '•'.repeat(Math.max(value.length - 4, 0)) + value.slice(-4) : '—';

interface RowProps {
  label: string;
  value?: string | number | null;
  wide?: boolean;
}

const Row = ({ label, value, wide = false }: RowProps) => (
  <div className={`ffu-item${wide ? ' ffu-item--wide' : ''}`}>
    <div className="ffu-label">{label}</div>
    <div className="ffu-value">{value || '—'}</div>
  </div>
);

const UserProfile = () => {
  const [profile, setProfile]       = useState<UserProfileData | null>(null);
  const [formData, setFormData]     = useState<UserProfileData | null>(null);
  const [error, setError]           = useState('');
  const [isEditing, setIsEditing]   = useState(false);
  const [saveLoading, setSaveLoading]   = useState(false);
  const [saveError, setSaveError]       = useState('');
  const [saveSuccess, setSaveSuccess]   = useState('');

  const [states, setStates]             = useState<StateItem[]>([]);
  const [statesLoading, setStatesLoading] = useState(false);
  const [cities, setCities]             = useState<CityItem[]>([]);
  const [cityLoading, setCityLoading]   = useState(false);
  const [cityDisabled, setCityDisabled] = useState(true);

  const [imageUploading, setImageUploading] = useState(false);
  const [imageError, setImageError]         = useState('');
  const fileInputRef = useRef<HTMLInputElement>(null);

  /* ── Fetch Profile ── */
  useEffect(() => {
    const fetchProfile = async () => {
      const token = localStorage.getItem('authToken');
      if (!token) { setError('You are not logged in.'); return; }
      try {
        const res  = await fetch(API_ENDPOINTS.USER_PROFILE, {
          headers: { Authorization: `Bearer ${token}`, 'Content-Type': 'application/json' },
        });
        const data = await res.json();
        if (res.ok && data.data) {
          setProfile(data.data);
          setFormData(data.data);
          localStorage.setItem('pu_name', data.data.pu_name);
        } else {
          setError(data.message || 'Failed to fetch profile.');
        }
      } catch { setError('An error occurred while fetching profile.'); }
    };
    fetchProfile();
  }, []);

  /* ── Fetch States ── */
  useEffect(() => {
    if (!isEditing) return;
    setStatesLoading(true);
    fetch(API_ENDPOINTS.GET_STATES, { headers: { 'Content-Type': 'application/json' } })
      .then(r => r.json())
      .then(d => { setStates(Array.isArray(d.states) ? d.states : []); setStatesLoading(false); })
      .catch(() => { setStates([]); setStatesLoading(false); });
  }, [isEditing]);

  /* ── Fetch Cities ── */
  useEffect(() => {
    if (!formData?.state_id) { setCities([]); setCityDisabled(true); return; }
    const fetchCities = async () => {
      setCityLoading(true); setCityDisabled(true);
      try {
        const res  = await fetch(`${API_ENDPOINTS.GET_CITY}?state_id=${formData.state_id}`,
          { headers: { 'Content-Type': 'application/json' } });
        const data = await res.json();
        setCities(Array.isArray(data.cities) ? data.cities : Array.isArray(data.data) ? data.data : []);
      } catch { setCities([]); }
      finally { setCityLoading(false); setCityDisabled(false); }
    };
    fetchCities();
  }, [formData?.state_id]);

  /* ── Handlers ── */
  const handleChange = (e: ChangeEvent<HTMLInputElement | HTMLSelectElement | HTMLTextAreaElement>) => {
    const { name, value } = e.target;
    if (name === 'state_id') setFormData(p => ({ ...p, state_id: value, city_id: '' }));
    else setFormData(p => ({ ...p, [name]: value }));
  };

  const handleEditToggle = () => {
    if (isEditing) { setFormData(profile); setSaveError(''); setSaveSuccess(''); setImageError(''); }
    setIsEditing(e => !e);
  };

  const handleSave = async () => {
    const token = localStorage.getItem('authToken');
    if (!token) return;
    setSaveLoading(true); setSaveError(''); setSaveSuccess('');
    try {
      const payload: Partial<UserProfileData> = {};
      if (formData.pu_name     !== profile.pu_name)     payload.pu_name     = formData.pu_name;
      if (formData.pud_address !== profile.pud_address) payload.pud_address = formData.pud_address;
      if (formData.state_id    !== profile.state_id)    payload.state_id    = formData.state_id;
      if (formData.city_id     !== profile.city_id)     payload.city_id     = formData.city_id;

      const res  = await fetch(API_ENDPOINTS.UPDATE_USER_PROFILE, {
        method: 'PATCH',
        headers: { Authorization: `Bearer ${token}`, 'Content-Type': 'application/json' },
        body: JSON.stringify(payload),
      });
      const data = await res.json();
      if (res.ok && data.status === 'success') {
        setProfile(p => ({ ...p, ...data.data }));
        setFormData(p => ({ ...p, ...data.data }));
        localStorage.setItem('pu_name', data.data.pu_name || formData.pu_name);
        setSaveSuccess('Profile updated successfully.');
        setIsEditing(false);
      } else {
        setSaveError(data.message || 'Failed to update profile.');
      }
    } catch { setSaveError('An error occurred while saving.'); }
    finally { setSaveLoading(false); }
  };

  const handleImageChange = async (e: ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;
    if (!['image/jpeg','image/jpg','image/png','image/webp'].includes(file.type)) {
      setImageError('Only JPEG, PNG, or WebP allowed.'); return;
    }
    if (file.size > 5 * 1024 * 1024) { setImageError('Image must be under 5 MB.'); return; }
    setImageError(''); setImageUploading(true);
    const token = localStorage.getItem('authToken');
    const fd = new FormData();
    fd.append('profile_image', file);
    try {
      const res  = await fetch(API_ENDPOINTS.UPLOAD_PROFILE_IMAGE, {
        method: 'POST', headers: { Authorization: `Bearer ${token}` }, body: fd,
      });
      const data = await res.json();
      if (res.ok && data.status === 'success') {
        const url = data.data.profile_image_url;
        setProfile(p => ({ ...p, profile_image_url: url }));
        setFormData(p => ({ ...p, profile_image_url: url }));
      } else { setImageError(data.message || 'Image upload failed.'); }
    } catch { setImageError('An error occurred during upload.'); }
    finally { setImageUploading(false); if (fileInputRef.current) fileInputRef.current.value = ''; }
  };

  const handleImageDelete = async () => {
    if (!profile?.profile_image_url) return;
    const token = localStorage.getItem('authToken');
    setImageUploading(true); setImageError('');
    try {
      const res  = await fetch(API_ENDPOINTS.UPLOAD_PROFILE_IMAGE, {
        method: 'DELETE', headers: { Authorization: `Bearer ${token}` },
      });
      const data = await res.json();
      if (res.ok && data.status === 'success') {
        setProfile(p => ({ ...p, profile_image_url: null }));
        setFormData(p => ({ ...p, profile_image_url: null }));
      } else { setImageError(data.message || 'Failed to remove image.'); }
    } catch { setImageError('An error occurred while removing image.'); }
    finally { setImageUploading(false); }
  };

  const avatarSrc = profile?.profile_image_url ||
    env.defaultAvatarUrl;

  const memberSince = formatMemberSince(profile?.created_at);
  const initial = (profile?.pu_name || 'U').charAt(0).toUpperCase();

  return (
    <main className="ffu-page">

      {/* ── Header band ── */}
      <header className="ffs-hero">
        <div className="ffh-container ffs-hero-inner">
          <div>
            <nav className="ffs-crumbs" aria-label="Breadcrumb">
              <Link to="/">Home</Link>
              <span aria-hidden="true">/</span>
              <span className="is-current">My profile</span>
            </nav>
            <h1 className="ffs-hero-title">My <em>Profile</em></h1>
          </div>
          <p className="ffs-hero-sub">Manage your details, photo and delivery location.</p>
        </div>
      </header>

      <div className="ffh-container">
        {error ? (
          <div className="ffu-state">
            <span className="ffu-state-icon" aria-hidden="true">!</span>
            <p>{error}</p>
          </div>
        ) : !profile ? (
          <div className="ffu-layout" aria-busy="true">
            <div className="ffu-skel ffu-skel--card" />
            <div className="ffu-skel ffu-skel--wide" />
          </div>
        ) : (
          <div className="ffu-layout">

            {/* ══ Identity card ══ */}
            <aside className="ffu-side">
              <div className="ffu-id">
                <div className="ffu-avatar-wrap">
                  {profile.profile_image_url ? (
                    <img src={avatarSrc} alt={`${profile.pu_name} profile`} className="ffu-avatar" />
                  ) : (
                    <span className="ffu-avatar ffu-avatar--letter" aria-hidden="true">{initial}</span>
                  )}
                  {imageUploading && (
                    <div className="ffu-avatar-busy"><span /></div>
                  )}
                  {isEditing && (
                    <button
                      type="button"
                      className="ffu-avatar-btn"
                      onClick={() => fileInputRef.current?.click()}
                      disabled={imageUploading}
                      aria-label="Upload photo"
                    >
                      <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
                        <path d="M4 8h3l2-3h6l2 3h3v11H4z" /><circle cx="12" cy="13" r="3.5" />
                      </svg>
                    </button>
                  )}
                </div>

                <h2 className="ffu-name">{profile.pu_name}</h2>
                <p className="ffu-email">{profile.pu_email}</p>
                <span className={`ffu-badge ${profile.is_deactive ? 'is-inactive' : 'is-active'}`}>
                  {profile.is_deactive ? 'Inactive' : 'Active'}
                </span>
                <p className="ffu-since">Member since <b>{memberSince}</b></p>

                {/* image actions (edit mode) */}
                {isEditing && (
                  <div className="ffu-img-actions">
                    <input
                      ref={fileInputRef}
                      type="file"
                      accept="image/jpeg,image/jpg,image/png,image/webp"
                      style={{ display: 'none' }}
                      onChange={handleImageChange}
                    />
                    <button
                      type="button"
                      className="ffh-btn ffh-btn--outline ffu-btn-sm"
                      onClick={() => fileInputRef.current?.click()}
                      disabled={imageUploading}
                    >
                      {imageUploading ? 'Uploading…' : 'Upload photo'}
                    </button>
                    {profile.profile_image_url && (
                      <button
                        type="button"
                        className="ffu-link ffu-link--danger"
                        onClick={handleImageDelete}
                        disabled={imageUploading}
                      >
                        Remove photo
                      </button>
                    )}
                    {imageError && <span className="ffu-img-error">{imageError}</span>}
                  </div>
                )}

                <button
                  type="button"
                  className={`ffh-btn ffu-edit${isEditing ? ' ffh-btn--outline' : ''}`}
                  onClick={handleEditToggle}
                >
                  {isEditing ? 'Cancel' : 'Edit profile'}
                </button>
              </div>

              <nav className="ffu-quick" aria-label="Account shortcuts">
                <Link to="/order-history">
                  <span>My orders</span><i aria-hidden="true">→</i>
                </Link>
                <Link to="/wishlist">
                  <span>Wishlist</span><i aria-hidden="true">→</i>
                </Link>
                <Link to="/cart">
                  <span>Cart</span><i aria-hidden="true">→</i>
                </Link>
                <Link to="/delete-account" className="is-danger">
                  <span>Delete account</span><i aria-hidden="true">→</i>
                </Link>
              </nav>
            </aside>

            {/* ══ Details ══ */}
            <div className="ffu-main">
              {saveSuccess && <div className="ffu-notice ffu-notice--ok" role="status">{saveSuccess}</div>}
              {saveError && <div className="ffu-notice ffu-notice--err" role="alert">{saveError}</div>}

              <section className="ffu-card">
                <div className="ffu-card-head">
                  <h3>Profile <em>information</em></h3>
                  {isEditing && <span className="ffu-hint">Email and phone can't be changed here</span>}
                </div>

                {!isEditing ? (
                  <div className="ffu-grid">
                    <Row label="Full name" value={profile.pu_name} />
                    <Row label="Email" value={profile.pu_email} />
                    <Row label="Phone" value={profile.pu_contact_no} />
                    <Row label="State" value={profile.state_name} />
                    <Row label="City" value={profile.city_name} />
                    <Row label="Address" value={profile.pud_address} wide />
                  </div>
                ) : (
                  <div className="ffu-grid">
                    <div className="ffu-field">
                      <label htmlFor="pu_name">Full name</label>
                      <input id="pu_name" name="pu_name" value={formData.pu_name || ''} onChange={handleChange} placeholder="Your name" />
                    </div>

                    <div className="ffu-field ffu-field--locked">
                      <label htmlFor="pu_email">Email</label>
                      <input id="pu_email" value={formData.pu_email || ''} readOnly disabled />
                    </div>

                    <div className="ffu-field ffu-field--locked">
                      <label htmlFor="pu_contact">Phone</label>
                      <input id="pu_contact" value={formData.pu_contact_no || ''} readOnly disabled />
                    </div>

                    <div className="ffu-field">
                      <label htmlFor="pud_address">Address</label>
                      <input id="pud_address" name="pud_address" value={formData.pud_address || ''} onChange={handleChange} placeholder="Your address" />
                    </div>

                    <div className="ffu-field">
                      <label htmlFor="state_id">State</label>
                      <select id="state_id" name="state_id" value={formData.state_id || ''} onChange={handleChange} disabled={statesLoading}>
                        <option value="">{statesLoading ? 'Loading…' : 'Select state'}</option>
                        {states.map((s) => (
                          <option key={s.state_id} value={s.state_id}>{s.state_name}</option>
                        ))}
                      </select>
                    </div>

                    <div className="ffu-field">
                      <label htmlFor="city_id">City</label>
                      <select id="city_id" name="city_id" value={formData.city_id || ''} onChange={handleChange} disabled={cityDisabled}>
                        <option value="">{cityLoading ? 'Loading…' : 'Select city'}</option>
                        {cities.map((c) => (
                          <option key={c.city_id} value={c.city_id}>{c.city_name}</option>
                        ))}
                      </select>
                    </div>

                    <div className="ffu-actions">
                      <button type="button" className="ffh-btn" onClick={handleSave} disabled={saveLoading}>
                        {saveLoading ? 'Saving…' : 'Save changes'}
                      </button>
                      <button type="button" className="ffh-btn ffh-btn--outline" onClick={handleEditToggle} disabled={saveLoading}>
                        Cancel
                      </button>
                    </div>
                  </div>
                )}
              </section>

              {/* ── Vendor ── */}
              {profile.created_by && (
                <section className="ffu-card">
                  <div className="ffu-card-head">
                    <h3>Vendor <em>information</em></h3>
                  </div>
                  <div className="ffu-grid">
                    <Row label="Username" value={profile.created_by.username} />
                    <Row label="Email" value={profile.created_by.email} />
                    <Row label="Full name" value={profile.created_by.fullname} />
                    <Row label="Contact" value={profile.created_by.ud_contact_no} />
                    <Row label="Aadhaar" value={maskId(profile.created_by.ud_aadhaar)} />
                    <Row label="PAN" value={maskId(profile.created_by.ud_pan)} />
                    <Row label="Address" value={profile.created_by.ud_address} wide />
                  </div>
                </section>
              )}
            </div>
          </div>
        )}
      </div>
    </main>
  );
};

export default UserProfile;